import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import { Checkbox, MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import type { LienFormValues } from "../../../LienTypes";

type MACAppealSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const MACAppealSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: MACAppealSectionProps) => {
  const hasMacAppeal = getValues("mac_appeal_by_date") !== null;
  const hasEscalation = getValues("request_for_escalation") ?? false;
  const decisionOptions = constants?.AppealResultChoices || [];

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        <div className="text-sm text-gray-500">MAC</div>
      </div>

      <ControlledDateInput
        control={control}
        name="mac_appeal_by_date"
        label="Appeal By Date"
        errors={{}}
        allowFutureDates={true}
        onSave={async (value) => {
          if (!value) {
            setValue("mac_appeal_file_date", null);
            setValue("alj_appeal_number", null);
            setValue("docket_number", null);
            setValue("mac_lien_amount", null);
            setValue("mac_appeal_amount", null);
            setValue("mac_appeal_amount_due", null);
            setValue("mac_decision_date", null);
            setValue("mac_decision_result", null);
            setValue("mac_decision_dollar_amount", null);
            setValue("mac_savings", null);
            setValue("request_for_escalation", false);
            setValue("request_for_escalation_date", null);
            setValue("escalation_order_date", null);
            await handleFieldChange(null, {
              mac_appeal_by_date: null,
              mac_appeal_file_date: null,
              alj_appeal_number: null,
              docket_number: null,
              mac_appeal_amount: null,
              mac_appeal_amount_due: null,
              mac_decision_date: null,
              mac_decision_result: null,
              mac_decision_dollar_amount: null,
              mac_savings: null,
              request_for_escalation: false,
              request_for_escalation_date: null,
              escalation_order_date: null,
            });
          } else {
            await handleFieldChange("mac_appeal_by_date", value);
          }
        }}
      />

      {hasMacAppeal && (
        <>
          <ControlledDateInput
            control={control}
            name="mac_appeal_file_date"
            label="Appeal File Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("mac_appeal_file_date", value)}
          />

          <ControlledInput
            control={control}
            name="alj_appeal_number"
            label="ALJ Appeal Number"
            errors={{}}
            onSave={(value) => handleFieldChange("alj_appeal_number", value)}
            componentType={TextInput}
          />

          <ControlledInput
            control={control}
            name="docket_number"
            label="Docket Number"
            errors={{}}
            onSave={(value) => handleFieldChange("docket_number", value)}
            componentType={TextInput}
          />

          <ControlledInput
            control={control}
            name="mac_lien_amount"
            label="Lien Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("mac_lien_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="mac_appeal_amount"
            label="Appeal Amount"
            errors={{}}
            onSave={(value) => handleFieldChange("mac_appeal_amount", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledInput
            control={control}
            name="mac_appeal_amount_due"
            label="Amount Due"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("mac_appeal_amount_due", value)
            }
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <ControlledDateInput
            control={control}
            name="mac_decision_date"
            label="Decision Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("mac_decision_date", value)}
          />

          <ControlledSelect
            control={control}
            name="mac_decision_result"
            label="Decision Result"
            errors={{}}
            options={decisionOptions}
            onSave={(value) =>
              handleFieldChange("mac_decision_result", value?.value || null)
            }
          />

          <ControlledInput
            control={control}
            name="mac_decision_dollar_amount"
            label="MAC Decision Dollar Amount"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("mac_decision_dollar_amount", value)
            }
            componentType={MaskedInput}
            mask="dollar"
          />

          <ControlledInput
            control={control}
            name="mac_savings"
            label="Savings"
            errors={{}}
            onSave={(value) => handleFieldChange("mac_savings", value)}
            componentType={MaskedInput}
            mask="dollar"
            readOnly
          />

          <Checkbox
            name="request_for_escalation"
            label="Request for Escalation"
            checked={getValues("request_for_escalation")}
            required={false}
            onChange={async (e) => {
              setValue("request_for_escalation", e.target.checked);
              if (!e.target.checked) {
                setValue("request_for_escalation_date", null);
                setValue("escalation_order_date", null);
                await handleFieldChange(null, {
                  request_for_escalation: false,
                  request_for_escalation_date: null,
                  escalation_order_date: null,
                });
              } else {
                await handleFieldChange(
                  "request_for_escalation",
                  e.target.checked,
                );
              }
            }}
          />

          {hasEscalation && (
            <>
              <ControlledDateInput
                control={control}
                name="request_for_escalation_date"
                label="Request for Escalation Date"
                errors={{}}
                allowFutureDates={true}
                onSave={(value) =>
                  handleFieldChange("request_for_escalation_date", value)
                }
              />

              <ControlledDateInput
                control={control}
                name="escalation_order_date"
                label="Escalation Order Date"
                errors={{}}
                allowFutureDates={true}
                onSave={(value) =>
                  handleFieldChange("escalation_order_date", value)
                }
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default MACAppealSection;
